import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Lottie from "lottie-react";
import * as approveAnimation from "../../../../assets/lotties/approve.json";
import { useUser } from "../../../../services/user-context";
import { getLabel, getUrl } from "../../utils/labels";
import { useApp } from "../../../../services/app-context";
import { isManager } from "../../../../services/utils";
import { userRoles } from "../../../../constant";
import { userCertificationsQueryKey } from "../../hooks/use-user-certifications";

interface CertificationSuccessProps {
  onComplete?: () => void;
}

export function CertificationSuccess({ onComplete }: CertificationSuccessProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userInfo } = useUser() as any;
  const userId = userInfo?.id || userInfo?.["sub"];
  const role = userInfo?.role;
  const { tenantProfile, certificationAssets } = useApp();
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);
  const labels = certificationAssets?.labels ?? {};

  useEffect(() => {
    const timer = setTimeout(async () => {
      // Partial key match, so both the infinite and single-page views refresh.
      await queryClient.refetchQueries({
        queryKey: userCertificationsQueryKey(userId),
      });
      if (onComplete) {
        onComplete();
      } else if (isManager(role)) {
        navigate(`/${url}?certificationType=${userRoles.AccountSpecialist}`);
      } else {
        navigate(`/${url}`);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [navigate, queryClient, userId, url, role, onComplete]);

  return (
    <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-8 px-4">
      <h1 className="text-large-title text-text-primary font-pp-bold text-center">
        {getLabel("certification.singular", labels)} Submitted
      </h1>

      {/* Approval Animation */}
      <div className="w-[200px] h-[200px] flex items-center justify-center">
        <Lottie
          animationData={approveAnimation}
          loop={false}
          className="w-full h-full"
        />
      </div>

      {/* Success message */}
      <div className="max-w-[600px] text-center">
        <p className="text-title2 font-pp-light text-text2">
          Nice work! Your {getLabel("certification.singular", labels)} has been submitted for review.
        </p>
      </div>
    </div>
  );
}
